import { NotificationsRepository } from './notifications.repository.js';
import { 
  Notification, 
  NotificationType,
  NotificationsResponse,
} from './notifications.schemas.js';
import {
  appEvents,
  AppEvent,
  BadgeUnlockedPayload,
  LevelUpPayload,
  PlanSharedPayload,
  CollaboratorAddedPayload,
  CommentAddedPayload,
} from '../../core/events/eventEmitter.js';
import { logger } from '../../utils/logger.js';
import { createLogger } from '../../common/utils/logger.js'

const notificationLogger = createLogger('NotificationsService')

export class NotificationsService {
  private readonly repository: NotificationsRepository;

  constructor() {
    this.repository = new NotificationsRepository();
    this.registerEventListeners();
  }

  /**
   * Register event listeners
   */
  private registerEventListeners(): void {
    notificationLogger.debug('Registering notification event listeners')
    
    // Badge unlocked
    appEvents.onEvent(AppEvent.BADGE_UNLOCKED, async (payload) => {
      const data = payload as BadgeUnlockedPayload;
      notificationLogger.info('Badge unlocked event', { userId: data.userId, badgeId: data.badgeId, badgeName: data.badgeName })
      await this.createNotification({
        userId: data.userId,
        type: NotificationType.BADGE_UNLOCKED,
        title: '🏆 Badge Unlocked!',
        message: `You unlocked the "${data.badgeName}" badge!`,
        data: { badgeId: data.badgeId },
        createdAt: data.timestamp,
      });
    });

    // Level up
    appEvents.onEvent(AppEvent.LEVEL_UP, async (payload) => {
      const data = payload as LevelUpPayload;
      notificationLogger.info('Level up event', { userId: data.userId, newLevel: data.newLevel })
      await this.createNotification({
        userId: data.userId,
        type: NotificationType.LEVEL_UP,
        title: '⬆️ Level Up!',
        message: `Congratulations! You reached level ${data.newLevel}!`,
        data: { level: data.newLevel },
        createdAt: data.timestamp,
      });
    });

    // Plan shared
    appEvents.onEvent(AppEvent.PLAN_SHARED, async (payload) => {
      const data = payload as PlanSharedPayload;
      notificationLogger.info('Plan shared event', { userId: data.recipientId, planId: data.planId, shareType: data.shareType })
      await this.createNotification({
        userId: data.recipientId,
        type: NotificationType.PLAN_SHARED,
        title: '📤 Plan Shared',
        message: `A plan was shared with you (${data.shareType} access)`,
        data: { planId: data.planId, shareType: data.shareType },
        createdAt: data.timestamp,
      });
    });

    // Collaborator added
    appEvents.onEvent(AppEvent.COLLABORATOR_ADDED, async (payload) => {
      const data = payload as CollaboratorAddedPayload;
      notificationLogger.info('Collaborator added event', { collaboratorId: data.collaboratorId, planId: data.planId, planName: data.planName })
      await this.createNotification({
        userId: data.collaboratorId,
        type: NotificationType.COLLABORATOR_ADDED,
        title: '👥 Added to Plan',
        message: `You were added to "${data.planName}"`,
        data: { planId: data.planId },
        createdAt: data.timestamp,
      });
    });

    // Comment added
    appEvents.onEvent(AppEvent.COMMENT_ADDED, async (payload) => {
      const data = payload as CommentAddedPayload;
      // Note: Notifying plan collaborators requires fetching plan details
      // This will be implemented when plan collaboration endpoints are available
      notificationLogger.debug('Comment added event received', { data });
    });

    notificationLogger.info('Notifications event listeners registered');
  }

  /**
   * Create notification
   */
  private async createNotification(
    notification: Omit<Notification, 'id' | 'read'>
  ): Promise<void> {
    try {
      notificationLogger.debug('Creating notification', { userId: notification.userId, type: notification.type })
      await this.repository.create({ ...notification, read: false });
      logger.info('Notification created', { 
        userId: notification.userId, 
        type: notification.type 
      });
    } catch (error) {
      logger.error('Failed to create notification', { error, notification });
      // Don't throw - notifications are non-critical
    }
  }

  /**
   * Get user notifications
   */
  async getNotifications(
    userId: string,
    options: {
      limit?: number;
      offset?: number;
      unreadOnly?: boolean;
    } = {}
  ): Promise<NotificationsResponse> {
    const [notifications, unreadCount] = await Promise.all([
      this.repository.getByUserId(userId, options),
      this.repository.countUnread(userId),
    ]);

    return {
      notifications,
      total: notifications.length,
      unreadCount,
    };
  }

  /**
   * Mark notifications as read
   */
  async markAsRead(notificationIds: string[]): Promise<void> {
    await this.repository.markAsRead(notificationIds);
  }

  /**
   * Mark all notifications as read
   */
  async markAllAsRead(userId: string, before?: number): Promise<number> {
    return this.repository.markAllAsRead(userId, before);
  }

  /**
   * Cleanup old notifications (run as scheduled job)
   */
  async cleanupOldNotifications(days: number = 30): Promise<number> {
    return this.repository.deleteOlderThan(days);
  }
}
